import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  BookOpen, 
  Search, 
  Users, 
  Target, 
  Sparkles, 
  ChevronRight, 
  ChevronLeft,
  Home,
  Compass
} from 'lucide-react';

interface AppTourProps {
  isOpen: boolean;
  onComplete: () => void;
}

interface TourStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight?: string;
}

const tourSteps: TourStep[] = [
  {
    icon: <Sparkles className="w-10 h-10 text-primary" />,
    title: "Welcome to Dino! 🦕",
    description: "Your new reading companion is here to help you track books, build reading habits, and connect with fellow readers. Let's take a quick tour!",
  },
  {
    icon: <Home className="w-10 h-10 text-primary" />,
    title: "Your Home Feed",
    description: "This is your home base! See updates from readers you follow, track your daily reading goals, and celebrate milestones together.",
    highlight: "home",
  },
  {
    icon: <Compass className="w-10 h-10 text-primary" />,
    title: "Discover New Books",
    description: "Get personalized book recommendations based on your reading taste. The more you rate, the better the suggestions become!",
    highlight: "discover",
  },
  {
    icon: <Search className="w-10 h-10 text-primary" />,
    title: "Find Any Book",
    description: "Search for books by title or author. Add them to your shelves, rate them, and write reviews to share your thoughts.",
    highlight: "search",
  },
  {
    icon: <Target className="w-10 h-10 text-primary" />,
    title: "Set Reading Goals",
    description: "Challenge yourself with daily, weekly, or yearly reading goals. Track your progress and build streaks for extra motivation!",
    highlight: "goals",
  },
  {
    icon: <Users className="w-10 h-10 text-primary" />,
    title: "Connect with Readers",
    description: "Follow friends and fellow book lovers. Cheer their progress, comment on reviews, and find your next read through their recommendations.",
    highlight: "social",
  },
  {
    icon: <BookOpen className="w-10 h-10 text-primary" />,
    title: "You're Ready! 📚",
    description: "Start by rating some books you've read. This helps Dino learn your taste and recommend books you'll love!",
  },
];

export function AppTour({ isOpen, onComplete }: AppTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  
  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      navigate('/discover');
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onComplete()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto p-4 rounded-full bg-primary/10 w-fit">
            {step.icon}
          </div>
          <DialogTitle className="text-xl font-heading">
            {step.title}
          </DialogTitle>
          <DialogDescription className="text-base leading-relaxed">
            {step.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Progress value={progress} className="h-2" />
          <p className="text-center text-sm text-muted-foreground mt-2">
            Step {currentStep + 1} of {tourSteps.length}
          </p>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2 w-full sm:w-auto">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                className="flex-1 sm:flex-none"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            {isFirstStep && (
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="flex-1 sm:flex-none text-muted-foreground"
              >
                Skip Tour
              </Button>
            )}
          </div>
          
          <Button
            onClick={handleNext}
            className="flex-1 sm:flex-none"
          >
            {isLastStep ? (
              <>
                Start Reading
                <BookOpen className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
